import { Capacitor } from '@capacitor/core';
import type { NavigateFunction } from 'react-router-dom';
import { setAuthSession, setRefreshToken, resetSessionEndGuard } from '@shared/auth/session';
import type { LDUserInfo } from '@shared/flags/FeatureFlagProvider';
import { PUSH_AFTER_LOGIN_FLAG } from '../constants/pushNotifications';
import { switchToCitizenView } from './switchView';

export interface LoginResponseData {
  token: string;
  /** Present on native only — web receives the refresh token as an HttpOnly cookie. */
  refreshToken?: string;
  user: {
    id: string;
    email: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
  };
  organization?: { id?: string; name?: string; subscription?: string } | null;
  membership?: { role?: string } | null;
  accountType?: 'citizen' | 'professional';
  hasDualAccess?: boolean;
  availableViews?: string[];
}

export interface LoginSuccessOptions {
  data: LoginResponseData;
  navigate: NavigateFunction;
  identifyUser: (user: LDUserInfo) => Promise<void>;
  returnUrl?: string;
  /** `citizen_enabled` flag. When false, citizens land on the coming-soon page. */
  citizenEnabled?: boolean;
}

export const handleLoginSuccess = ({
  data,
  navigate,
  identifyUser,
  returnUrl = '/dashboard',
  citizenEnabled = false,
}: LoginSuccessOptions) => {
  const { token, refreshToken, user, membership, organization, accountType, hasDualAccess, availableViews } = data;
  const isCitizen = accountType === 'citizen';

  // Fresh login: re-arm the session-end guard (a prior expiry disabled it) and
  // store the native refresh token (no-op on web, which uses the cookie).
  resetSessionEndGuard();
  if (refreshToken) setRefreshToken(refreshToken);

  identifyUser({
    id: user.id,
    email: user.email,
    displayName: user.displayName || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    username: user.username,
    role: membership?.role,
    accountType,
    organizationId: organization?.id,
    organizationName: organization?.name,
    subscription: organization?.subscription,
  }).catch(() => {
    // Non-blocking — flag targeting degrades to anonymous defaults
  });

  if (hasDualAccess) {
    localStorage.setItem('hasDualAccess', 'true');
  }

  if (isCitizen) {
    setAuthSession({ token, user, accountType: 'citizen', availableViews });

    // Citizen app is dark. Keep the session (so the account works the moment the
    // flag flips) but send them to /citizen, which renders the coming-soon page.
    if (!citizenEnabled) {
      window.location.href = '/citizen';
      return;
    }

    // On native, prompt for push permission once on the next mount (CitizenApp
    // reads this flag). sessionStorage survives the location.href reload below
    // but not a cold start, so push is never auto-prompted on relaunch.
    if (Capacitor.isNativePlatform()) {
      sessionStorage.setItem(PUSH_AFTER_LOGIN_FLAG, '1');
    }

    if (hasDualAccess && !localStorage.getItem('preferredMode')) {
      window.location.href = '/citizen/choose-mode';
      return;
    }
    const citizenReturnUrl = returnUrl.startsWith('/citizen') ? returnUrl : '/citizen/home';
    window.location.href = citizenReturnUrl;
    return;
  }

  setAuthSession({
    token,
    user,
    membership: membership ?? undefined,
    includeRoleFromMembership: true,
    // /select-view and the profile-menu view switch read this from storage; the
    // server's login response is the only place it comes from.
    availableViews,
  });

  if (Capacitor.isNativePlatform()) {
    // ProTabLayout reads this once after navigate to request push permission only
    // on a fresh sign-in — not on cold start with a restored session token.
    sessionStorage.setItem(PUSH_AFTER_LOGIN_FLAG, '1');
    navigate('/pro/home');
    return;
  }

  // A login that started on a citizen URL is an explicit choice of the citizen
  // view. Honor it for accounts that have one instead of classifying dual-access
  // accounts as professional and bouncing them through /select-view.
  const wantsCitizen = returnUrl === '/citizen' || returnUrl.startsWith('/citizen/');
  const hasCitizenView = hasDualAccess || (availableViews ?? []).includes('citizen');
  if (wantsCitizen && hasCitizenView && citizenEnabled) {
    // Bare /citizen is the public landing screen; send the signed-in user home.
    switchToCitizenView(returnUrl === '/citizen' ? '/citizen/home' : returnUrl);
    return;
  }

  navigate(returnUrl);
};
